/**
 * Source-selection model shared by the picker, the Explorer, and the batch
 * Data tab. A selection is one (role, facility) pair; hub roles carry a null
 * facility, dairy roles carry a facility identifier.
 */
import type { SeriesParams } from "../../api/hooks";
import type { SourcesCatalogResponse } from "../../api/types";

export type Selection = { role: string; facility: string | null };

export function selectionKey(s: Selection): string {
  return `${s.role}::${s.facility ?? ""}`;
}

export function hasSelection(list: Selection[], s: Selection): boolean {
  return list.some((x) => selectionKey(x) === selectionKey(s));
}

export function toggleSelection(list: Selection[], s: Selection): Selection[] {
  return hasSelection(list, s)
    ? list.filter((x) => selectionKey(x) !== selectionKey(s))
    : [...list, s];
}

/**
 * Human label for a selection, resolving both halves from the catalog.
 *
 * A dairy role's label is scoped to its section ("Outlet volume"), so a pill
 * naming only the label would be ambiguous against the hub's. Prefix the
 * section for those, giving "Per-dairy outlet volume · Ackerman Dairy".
 */
export function selectionLabel(s: Selection, catalog: SourcesCatalogResponse | undefined): string {
  const entry = catalog?.sources.find((source) => source.role === s.role);
  if (!s.facility) return entry?.label ?? roleDisplayName(s.role);
  const roleLabel = entry
    ? `${entry.group_label} ${entry.label.toLowerCase()}`
    : roleDisplayName(s.role);
  const facility = catalog?.facilities.find((f) => f.identifier === s.facility);
  return `${roleLabel} · ${facility?.display_name ?? s.facility}`;
}

/**
 * Domain tokens that render as fixed-case acronyms rather than title-cased
 * (so `pge_energy_mmbtu` → "PGE Energy MMBtu", not "Pge Energy Mmbtu").
 */
const ROLE_ACRONYMS: Record<string, string> = {
  pge: "PGE",
  mmbtu: "MMBtu",
  scf: "SCF",
  mscf: "MSCF",
  hhv: "HHV",
  lhv: "LHV",
  ch4: "CH₄",
  co2e: "CO₂e",
};

/**
 * Title-case a role key.
 *
 * A LAST-RESORT fallback only, for the window before `GET /data/sources` has
 * resolved. The catalog carries a curated `label` and that is what the UI shows.
 * This function is why the XRC2 plant meter used to read "PGE Outlet Volume":
 * a key is an identifier, not a name, and title-casing one does not make it one.
 */
export function roleDisplayName(role: string): string {
  return role
    .split("_")
    .map((w) => ROLE_ACRONYMS[w] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Map selections + a gas-day range into the params `useDataSeriesMany` wants. */
export function selectionsToParams(
  selections: Selection[],
  start: string,
  end: string,
  granularity: "daily" | "hourly" = "daily",
): SeriesParams[] {
  return selections.map((s) => ({
    role: s.role,
    facility: s.facility ?? undefined,
    start,
    end,
    granularity,
  }));
}

// ── URL encoding (the Explorer keeps selection in searchParams) ─────────────
//
// `role:facility` joined by commas; hub roles omit the facility part.

export function encodeSelections(selections: Selection[]): string {
  return selections.map((s) => (s.facility ? `${s.role}:${s.facility}` : s.role)).join(",");
}

export function decodeSelections(raw: string | null): Selection[] {
  if (!raw) return [];
  const out: Selection[] = [];
  const seen = new Set<string>();
  for (const token of raw.split(",")) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const [role, facility] = trimmed.split(":");
    if (!role) continue;
    const sel: Selection = { role, facility: facility || null };
    const key = selectionKey(sel);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(sel);
  }
  return out;
}
